'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import Modal from '@/shared/components/overlay/modal';
import { Button } from '@/shared/components/primitives/button';
import { readClientCookie } from '@/shared/utils/client-cookies';
import { VERIFICATION_LAST_RESEND_COOKIE } from '@/features/auth/shared/helpers/constants/verification-cookies';
import {
  LazyCreateAccountForm,
  LazyLoginFormContainer,
  LazyVerifyEmailContent,
} from '@/features/auth/shared/components/auth-gate/auth-gate-lazy-views';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { fetchVerificationStatus } from '@/features/auth/shared/api/verification-status-request';
import { hasActiveSession } from '@/features/auth/shared/api/session-request';
import { MessageBox } from '@/shared/components/message-box';
import {
  claimPendingGateAction,
  onPendingGateActionClaimed,
} from '@/features/auth/shared/state/pending-gate-action';
import { runPendingGateAction } from '@/features/auth/shared/helpers/run-pending-gate-action';
import {
  AUTH_GATE_MODAL_KEY,
  chooseAuthGateMethod,
  closeAuthGate,
  EMAIL_SIGNUP_METHOD,
  trackAuthGateCompleted,
  TRIGGER_BY_PENDING_ACTION,
  useAuthGateStore,
} from '@/features/auth/shared/state/auth-gate.store';

const LOGIN_TITLE_ID = 'auth-gate-login-title';
const REGISTER_TITLE_ID = 'auth-gate-register-title';

export const AuthGateModal = () => {
  const t = useTranslations('Auth.authGate');
  const pathname = usePathname();
  const view = useAuthGateStore((s) => s.view);
  const prefillEmail = useAuthGateStore((s) => s.prefillEmail);
  const hideSocial = useAuthGateStore((s) => s.hideSocial);
  const redirectUrl = useAuthGateStore((s) => s.redirectUrl);
  const pendingAction = useAuthGateStore((s) => s.pendingAction);
  const origin = useAuthGateStore((s) => s.origin);
  const trigger = useAuthGateStore((s) => s.trigger);
  const setView = useAuthGateStore((s) => s.setView);
  const setPrefillEmail = useAuthGateStore((s) => s.setPrefillEmail);

  const { authenticatedUser } = useUser();
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [statusNotice, setStatusNotice] = useState<'unverified' | 'failed' | null>(null);

  const initialPathname = useRef(pathname);
  useEffect(() => {
    if (pathname === initialPathname.current) return;
    initialPathname.current = pathname;
    closeAuthGate();
  }, [pathname]);

  const claims = useAuthGateStore((s) => s.claims);
  useEffect(() => {
    if (!claims) return;

    return onPendingGateActionClaimed(() => {
      useAuthGateStore.getState().markResolved();
      closeAuthGate();
    });
  }, [claims]);

  const completeGate = useCallback(async () => {
    // Nothing is released until the server confirms a session; a verified address alone is not one.
    if (!(await hasActiveSession())) {
      setView('login');
      return;
    }

    const action = pendingAction;
    const { claims, method: chosenMethod } = useAuthGateStore.getState();
    const claim = claimPendingGateAction();

    const claimedElsewhere = claims && claim.status === 'gone';

    const resumable = claim.status === 'claimed' ? claim.action : null;

    const method = chosenMethod ?? (claim.status === 'claimed' ? claim.method : null);

    useAuthGateStore.getState().markResolved();
    trackAuthGateCompleted({
      trigger: trigger ?? (resumable ? TRIGGER_BY_PENDING_ACTION[resumable.type] : null),
      resumed: !claimedElsewhere && Boolean(action || resumable),
      method,
      outcome: 'completed',
    });

    if (!claimedElsewhere) {
      if (action) {
        action();
      } else if (resumable) {
        runPendingGateAction(resumable);
      }
    }
    closeAuthGate();
  }, [pendingAction, trigger, setView]);

  const handleAlreadyVerified = useCallback(async () => {
    if (authenticatedUser) {
      await completeGate();
      return;
    }

    if (origin !== 'client') {
      setView('login');
      return;
    }

    setCheckingVerification(true);
    const status = await fetchVerificationStatus();
    setCheckingVerification(false);

    if (status === 'verified') {
      // Verified in an external browser, so this webview has no session: sign in first, as on web.
      setView('login');
      return;
    }
    // A failed check says nothing about the address.
    setStatusNotice(status === 'failed' ? 'failed' : 'unverified');
  }, [authenticatedUser, completeGate, origin, setView]);

  const showVerificationNotice = useCallback(
    (email: string) => {
      setStatusNotice(null);
      setPrefillEmail(email);
      setView('verifySent');
    },
    [setPrefillEmail, setView],
  );

  const title =
    view === 'register' || view === 'verifySent'
      ? t('registerTitle')
      : view === 'success'
        ? t('successTitle')
        : t('loginTitle');
  const titleId = view === 'login' ? LOGIN_TITLE_ID : REGISTER_TITLE_ID;
  const contentClassName = view === 'verifySent' ? 'px-2 pb-6' : 'px-2 py-6';

  return (
    <Modal
      modalKey={AUTH_GATE_MODAL_KEY}
      closeOnBackdropClick
      onClose={closeAuthGate}
      header={
        <h2 id={titleId} className='font-heading w-full pl-9 text-center text-lg'>
          {title}
        </h2>
      }
    >
      <div className={contentClassName}>
        {view === 'login' && (
          <LazyLoginFormContainer
            titleId={titleId}
            redirectUrl={redirectUrl}
            hideSocialButtons={hideSocial}
            defaultEmail={prefillEmail}
            onSuccess={() => {
              chooseAuthGateMethod('login', 'succeeded');
              void completeGate();
            }}
            onSwitchToRegister={() => setView('register')}
            onEmailNotVerified={showVerificationNotice}
            onInClientSuccess={() => void completeGate()}
            onSocialStart={(provider) => chooseAuthGateMethod(`social_${provider}`, 'started')}
          />
        )}

        {view === 'register' && (
          <LazyCreateAccountForm
            titleId={titleId}
            redirectUrl={redirectUrl}
            hideSocialButtons={hideSocial}
            defaultEmail={prefillEmail}
            onSuccess={(email) => {
              chooseAuthGateMethod(EMAIL_SIGNUP_METHOD, 'succeeded');
              showVerificationNotice(email);
            }}
            onSwitchToLogin={() => setView('login')}
            onInClientSuccess={() => void completeGate()}
            onSocialStart={(provider) => chooseAuthGateMethod(`social_${provider}`, 'started')}
          />
        )}

        {view === 'success' && (
          <div className='flex flex-col items-center gap-3 px-4 text-center'>
            <CheckCircle2 className='text-success h-12 w-12' aria-hidden='true' />
            <p aria-live='polite'>{t('successMessage')}</p>
          </div>
        )}

        {view === 'verifySent' && (
          <LazyVerifyEmailContent
            initialEmail={prefillEmail || null}
            returnTo={redirectUrl || undefined}
            verificationError={null}
            initialResendStatus={
              readClientCookie(VERIFICATION_LAST_RESEND_COOKIE) ? 'cooldown' : 'idle'
            }
            extraContent={
              <div className='mx-auto flex w-full max-w-96 flex-col items-center gap-3'>
                {statusNotice && (
                  <MessageBox
                    type={statusNotice === 'failed' ? 'warning' : 'info'}
                    className='w-full'
                    closable={false}
                  >
                    {statusNotice === 'failed' ? t('verificationCheckFailed') : t('notVerifiedYet')}
                  </MessageBox>
                )}
                <Button
                  type='button'
                  variant='secondary'
                  size='md'
                  disabled={checkingVerification}
                  onClick={() => {
                    void handleAlreadyVerified();
                  }}
                >
                  {checkingVerification ? t('checkingVerification') : t('alreadyVerified')}
                </Button>
              </div>
            }
          />
        )}
      </div>
    </Modal>
  );
};
